<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatDayMonth } from '@/utils/format'
import type { AmortizationFeedback } from './types'

import BFeedbackPage from '@/components/base/BFeedbackPage/BFeedbackPage.vue'
import type { FeedbackState } from '@/components/base/BFeedbackPage/types'

// Feedback window of the amortization dialog (ADA-4437 etapp 5), on the shared BFeedbackPage
// (the feedback pattern Anton approved for the respite dialog in etapp 4). The date is the
// plan's first payment day from the submitted payload. A request sent to the creditor has no
// plan and no date: it points the customer to Messages, where the creditor's answer arrives.
const { feedback, date } = defineProps<{
  feedback: AmortizationFeedback | null
  date: Date | null
}>()

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

const title = computed(() => (feedback != null ? t(`common.amortization_${feedback}_title`) : ''))
const formattedDate = computed(() => (date != null ? formatDayMonth(date) : ''))

// Waiting on the creditor is neither a success nor a failure — the shared page renders it as
// pending, without the state icon.
const state = computed<FeedbackState | null>(() => (feedback === 'sent_to_creditor' ? 'pending' : feedback))
</script>

<template>
  <b-feedback-page
    :feedback="state"
    :icon="feedback === 'sent_to_creditor' ? null : undefined"
    :title="title"
    :button-text="t('common.return_to_debts')"
    @close="emit('close')"
  >
    <i18n-t
      v-if="feedback === 'success'"
      keypath="common.amortization_success_text"
      tag="span"
    >
      <span class="font-weight-bold">{{ formattedDate }}</span>
    </i18n-t>
    <template v-else-if="feedback === 'sent_to_creditor'">{{
      t('common.amortization_sent_to_creditor_text')
    }}</template>
    <template v-else-if="feedback === 'failed'">{{ t('common.amortization_failed_text') }}</template>
  </b-feedback-page>
</template>
